import React, { useState, FunctionComponent, SVGProps, forwardRef } from 'react';
import Select, { SelectProps as originalSelectProps } from '@mui/material/Select';
import {
  MenuItem,
  InputLabel,
  FormControlLabel,
  FormControl,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import styles from './index.module.scss';
import { isEmpty } from 'lodash';
import TextField from '../TextField';
import CheckboxField from '../CheckboxField';
import classNames from 'classnames';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/arrow.svg';

export interface MultipleSelectProps extends originalSelectProps {
  options: {
    value: any; // eslint-disable-line
    label: string | React.ReactNode;
    disabled?: boolean;
    image?: string;
  }[];
  isReadonly?: boolean;
  clearAll?(): void;
  allowSearch?: boolean;
  placeholder?: string;
  addAllText?: string;
  selected: string[];
  name?: string;
  width?: string | number;
  selectClassName?: string;
  wrapperClassName?: string;
  customIcon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  selectClasses?: {
    selectRoot?: string;
    checkIcon?: string;
    selected?: string;
  };
  disablePortal?: boolean;
}
export default forwardRef((props: MultipleSelectProps, ref) => {
  const {
    name,
    selected,
    label,
    options,
    isReadonly,
    wrapperClassName,
    clearAll,
    placeholder,
    addAllText,
    allowSearch,
    width,
    selectClassName,
    required,
    customIcon,
    iconClassName,
    selectClasses,
    disablePortal = false,
    ...selectProps
  } = props;
  const [checked, setChecked] = useState(false);
  const [search, setSearch] = useState<string>('');
  const isAllSelected = !isEmpty(options) && selected.length === options.length;
  return (
    <div className={classNames(styles.root, wrapperClassName, 'flex flex-col')}>
      <FormControl fullWidth>
        <InputLabel id={`label-for-${name}`}>
          {label}
          {required ? <span>*</span> : null}
        </InputLabel>
        {placeholder ? <div className={styles.placeholder}>{placeholder}</div> : null}
        {isReadonly ? (
          <div>{options.find((x) => x.value === selected)?.label}</div>
        ) : (
          <Select
            MenuProps={{ autoFocus: false, disablePortal }}
            multiple
            inputRef={ref}
            name={name}
            {...selectProps}
            className={selectClasses?.selectRoot}
            classes={{
              icon: iconClassName ? iconClassName : styles.icon,
              select: selectClassName ? selectClassName : styles.select,
            }}
            style={{ marginTop: 0, width: width ? width : 215 }}
            disableUnderline
            {...(allowSearch && {
              onChange: (e, child) => {
                // Filter out when value is undefined (in case of selecting text field)
                e.target.value = (e?.target?.value as string[])?.filter((x) => x !== undefined);
                if (props?.onChange) {
                  props?.onChange(e, child);
                }
              },
              onClose: () => {
                setSearch('');
                if (props?.onClose) {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  props?.onClose();
                }
              },
            })}
            IconComponent={customIcon ? customIcon : ArrowIcon}
            value={selected}
          >
            {allowSearch ? (
              <TextField
                placeholder="Search"
                autoFocus
                onChange={(e) => setSearch(e.target.value)}
                type="search"
                onKeyDown={(e) => {
                  // Prevents autoselecting item while typing (default Select behaviour)
                  e.stopPropagation();
                }}
                wrapperClassName="mr-4 p-4"
                size="small"
                variant="outlined"
              />
            ) : null}
            {clearAll && (
              <div className="flex flex-row justify-start pl-4  pb-2 cursor-pointer">
                <FormControlLabel
                  control={
                    <CheckboxField
                      checked={checked}
                      onChange={(event) => {
                        setChecked(event.target.checked);
                        if (event.target.checked) {
                          clearAll();
                          setChecked(false);
                        }
                      }}
                      color="primary"
                    />
                  }
                  label="Clear All"
                />
              </div>
            )}
            {options
              ?.filter(
                (option) =>
                  (typeof option?.label === 'string' &&
                    option.label?.toLowerCase().includes(search?.toLowerCase())) ||
                  search === '',
              )
              ?.map((x) => (
                <MenuItem
                  key={JSON.stringify(x.value)}
                  value={x.value}
                  disabled={x.disabled}
                  classes={{
                    selected: selectClasses?.selected ?? '',
                  }}
                  className="checkbox-select"
                >
                  <ListItemIcon className={selectClasses?.checkIcon ?? ''}>
                    <CheckboxField checked={selected.indexOf(x.value) > -1} color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={x.label} />
                  {x?.image ? <Avatar src={x.image} className={styles.avatar} /> : null}
                </MenuItem>
              ))}
            {addAllText ? (
              <MenuItem value="all">
                <ListItemIcon>
                  <CheckboxField checked={isAllSelected} color="primary" />
                </ListItemIcon>
                <ListItemText primary={addAllText} />
              </MenuItem>
            ) : null}
          </Select>
        )}
      </FormControl>
    </div>
  );
});
